import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsEnvelopePaper, BsInfoCircle, BsSliders } from 'react-icons/bs';

import Card from '#globalComponents/Card/Card';
import Checkbox from '#globalComponents/Checkbox/Checkbox';
import FormField from '#globalComponents/Forms/FormField';
import TextInput from '#globalComponents/Forms/TextInput';

import SignatureSelector from './SignatureSelector/SignatureSelector';

const AdvancedSettingsTab = ({
	maxEmailsPerDay,
	signatureId,
	showUnsubscribeLink,
	onSettingsChange,
	onSignatureChange,
}) => {
	const [dailyLimit, setDailyLimit] = useState(maxEmailsPerDay || 20);
	const [unsubscribeEnabled, setUnsubscribeEnabled] = useState(showUnsubscribeLink || false);

	const handleLimitChange = (e) => {
		const value = e.target.value;
		// Only allow positive integers
		if (value === '' || (/^\d+$/.test(value) && parseInt(value, 10) > 0)) {
			setDailyLimit(value);
			// Automatically notify parent of changes
			onSettingsChange({ maxEmailsPerDay: parseInt(value, 10) });
		}
	};

	return (
		<Card>
			<div className="flex flex-col space-y-1.5 mb-6">
				<div className="flex items-center gap-2">
					<BsSliders className="h-5 w-5 text-bgBase-800" />
					<h3 className="text-lg font-semibold leading-none tracking-tight">Erweiterte Einstellungen</h3>
				</div>
				<p className="text-sm text-bgBase-800">Zusätzliche Einstellungen zur Steuerung des E-Mail-Versands</p>
			</div>

			<div className="space-y-6">
				{/* Daily Limit Section */}
				<div className="space-y-3">
					<FormField
						label="Maximale E-Mails pro Tag"
						htmlFor="maxEmailsPerDay"
						hint="Sobald dieses Limit erreicht ist, werden weitere E-Mails auf den nächsten Tag verschoben"
					>
						<TextInput
							id="maxEmailsPerDay"
							type="number"
							min="1"
							value={dailyLimit}
							onChange={handleLimitChange}
							placeholder="z.B. 20"
							className="max-w-xs"
						/>
					</FormField>

					<div className="p-4 bg-primary-100 border border-primary-200 rounded-card">
						<div className="flex gap-3">
							<BsInfoCircle className="h-5 w-5 text-primary-700 flex-shrink-0 mt-0.5" />
							<div className="space-y-1">
								<h5 className="text-sm font-medium text-primary-700">Hinweis zum täglichen Limit</h5>
								<p className="text-sm text-primary-700">
									Ein niedriges Limit schützt die Reputation Ihrer E-Mail-Adresse und verhindert, dass
									Sie als Spam markiert werden.
								</p>
							</div>
						</div>
					</div>
				</div>

				{/* Unsubscribe Link Section */}
				<div className="space-y-3 pt-6 border-t">
					<FormField label="Abmelde-Link">
						<div className="flex items-start gap-3 p-4 border rounded-card hover:bg-bgBase-300 transition-colors">
							<div className="mt-0.5">
								<Checkbox
									id="showUnsubscribeLink"
									checked={unsubscribeEnabled}
									onChange={(checked) => {
										setUnsubscribeEnabled(checked);
										onSettingsChange({ showUnsubscribeLink: checked });
									}}
								/>
							</div>
							<label htmlFor="showUnsubscribeLink" className="cursor-pointer flex-1">
								<p className="text-sm font-medium">Abmelde-Link in E-Mails anzeigen</p>
								<p className="text-xs text-bgBase-800 mt-1">
									Fügt einen kleinen &quot;Abmelden&quot;-Link am Ende jeder E-Mail ein. Empfänger
									können sich damit von dieser Kampagne abmelden und erhalten keine weiteren
									Follow-Up-Mails.
								</p>
							</label>
						</div>
					</FormField>
				</div>

				{/* Signature Section */}
				<div className="space-y-3 pt-6 border-t">
					<div className="flex items-center gap-2">
						<BsEnvelopePaper className="h-5 w-5 text-bgBase-800" />
						<label className="text-sm font-medium leading-none">E-Mail-Signatur</label>
					</div>
					<p className="text-xs text-bgBase-800">
						Die Signatur wird automatisch an alle E-Mails dieser Kampagne angehängt
					</p>
					<SignatureSelector value={signatureId} onChange={onSignatureChange} showEditor={true} />
				</div>
			</div>
		</Card>
	);
};

AdvancedSettingsTab.propTypes = {
	maxEmailsPerDay: PropTypes.number,
	signatureId: PropTypes.string,
	showUnsubscribeLink: PropTypes.bool,
	onSettingsChange: PropTypes.func.isRequired,
	onSignatureChange: PropTypes.func,
};

AdvancedSettingsTab.defaultProps = {
	maxEmailsPerDay: 20,
	signatureId: null,
	showUnsubscribeLink: false,
};

export default AdvancedSettingsTab;
